import { FC } from 'react';
import { css } from '@emotion/react';
import { mapKeys, camelCase } from 'lodash';
import { colors, spacing, fontStyles } from '../../../styles';
import { TimeRange } from '../../../types';
import { InvestmentTable, ColumnSelection, ColumnSelectionType } from '../../InvestmentTable';
import { CollectionInvestment } from '../../../apis/backend';
import { ToolRendererProps } from './types';
import { LoadingState } from './shared/LoadingState';
import { SENTIMENT_COLORS } from './shared/constants';

// Shared helper to transform investment data for InvestmentTable
const transformInvestmentData = (investment: CollectionInvestment): any => {
  const transformed = mapKeys(investment, (_value, key) => camelCase(key));
  // Fix field name mismatch: backend returns "price" but InvestmentTable expects "latestPrice"
  if (transformed.price && !transformed.latestPrice) {
    transformed.latestPrice = transformed.price;
  }
  return transformed;
};

// Filter Stocks Renderer
export const FilterStocksRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return <LoadingState message="Filtering stocks..." />;
  }

  const results = output.results as CollectionInvestment[] | undefined;
  const metadata = output.metadata as any;
  const filterExpressions = metadata?.filter_expressions || output.filters_applied || [];
  const totalResults = metadata?.total_results || output.total_matches || (results ? results.length : 0);
  const error = output.error;

  // Handle error case
  if (error) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${SENTIMENT_COLORS.negative};
        `}
      >
        Error: {error}
      </div>
    );
  }

  // Handle no results
  if (!results || (Array.isArray(results) && results.length === 0)) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${colors.silver};
        `}
      >
        No stocks matched the filter criteria
      </div>
    );
  }

  // Transform snake_case keys to camelCase for InvestmentTable compatibility
  const investments = results.map(transformInvestmentData);

  // Configure column selections for filter results
  const columnSelections: ColumnSelection[] = [
    { type: ColumnSelectionType.Name },
    { type: ColumnSelectionType.BottomLine },
    { type: ColumnSelectionType.LatestPrice },
    { type: ColumnSelectionType.ChangePercent, timeRange: TimeRange.days1 },
    { type: ColumnSelectionType.MarketCap },
  ];

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0 0 0;
      `}
    >
      {/* Filter Header */}
      <div
        css={css`
          margin-bottom: ${spacing.deci};
        `}
      >
        <div
          css={css`
            ${fontStyles.flea}
            color: ${colors.silver};
            display: flex;
            gap: ${spacing.centi};
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: ${spacing.milli};
          `}
        >
          {filterExpressions.length > 0 && (
            <div
              css={css`
                display: flex;
                gap: 4px;
                flex-wrap: wrap;
              `}
              role="list"
              aria-label="Applied filters"
            >
              {filterExpressions.map((expr: string, index: number) => (
                <span
                  key={index}
                  role="listitem"
                  aria-label={`Filter: ${expr}`}
                  css={css`
                    background: ${colors.petrolBlue};
                    color: white;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 10px;
                    font-weight: 500;
                    max-width: 250px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;

                    html[data-theme='dark'] & {
                      background: ${colors.skyBlue};
                      color: ${colors.liquorice};
                    }
                  `}
                  title={expr}
                >
                  {expr}
                </span>
              ))}
            </div>
          )}
          <span>
            <span
              css={css`
                font-family: monospace;
                font-variant-numeric: tabular-nums;
              `}
            >
              {totalResults}
            </span>{' '}
            stock{totalResults === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Investment Table */}
      <InvestmentTable
        investments={investments}
        columnSelections={columnSelections}
        sortingRule={{ id: 'marketCap', desc: true }}
        hideColumnControls={true}
        showBookmark={false}
        isLoading={false}
      />
    </div>
  );
};
